import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../hooks/useAuth';
import {
  Send, CheckCircle, Clock, TrendingUp, Eye, AlertCircle,
  Bell, ExternalLink, RefreshCw, ChevronRight, Filter
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

const STATUS_STYLES = {
  draft:             'bg-slate-800 text-slate-400',
  sent:              'bg-blue-500/15 text-blue-400 border border-blue-500/30',
  partially_signed:  'bg-amber-500/15 text-amber-400 border border-amber-500/30',
  completed:         'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
  expired:           'bg-red-500/15 text-red-400 border border-red-500/30',
  voided:            'bg-slate-700 text-slate-500',
};

const STATUS_LABELS = {
  draft: 'Draft', sent: 'Sent', partially_signed: 'Partial',
  completed: 'Completed', expired: 'Expired', voided: 'Voided'
};

function KPICard({ label, value, icon: Icon, color, sub }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex items-start justify-between group hover:border-slate-700 transition-colors">
      <div>
        <div className="text-sm text-slate-500 mb-1 font-medium">{label}</div>
        <div className="text-3xl font-bold text-white tracking-tight">{value ?? '—'}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [docs, setDocs] = useState([]);
  const [followUp, setFollowUp] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, docsRes, followRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get(`/dashboard/documents?status=${statusFilter}&limit=15`),
        api.get('/dashboard/follow-up'),
      ]);
      setStats(statsRes.data);
      setDocs(docsRes.data.data || []);
      setFollowUp(followRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [statusFilter]);

  const sendReminder = async (docId) => {
    setSending(s => ({ ...s, [docId]: true }));
    try {
      await api.post(`/documents/${docId}/remind`);
      alert('Reminder sent!');
    } catch (e) {
      alert('Failed to send reminder');
    } finally {
      setSending(s => ({ ...s, [docId]: false }));
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {format(new Date(), 'EEEE, MMMM d, yyyy')}
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={loadData} className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition-colors">
            <RefreshCw size={16} />
          </button>
          <button
            onClick={() => navigate('/documents/new')}
            className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            <Send size={15} /> Send Document
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <KPICard label="Total Sent" value={stats?.total_sent} icon={Send} color="bg-blue-600" />
        <KPICard label="Pending Signatures" value={stats?.pending} icon={Clock} color="bg-amber-500" />
        <KPICard label="Completed" value={stats?.completed} icon={CheckCircle} color="bg-emerald-600" />
        <KPICard
          label="Conversion Rate"
          value={stats?.conversion_rate ? `${stats.conversion_rate}%` : '—'}
          icon={TrendingUp}
          color="bg-purple-600"
          sub="signed / sent"
        />
      </div>

      {/* Follow-up Alert */}
      {followUp.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle size={18} className="text-amber-400" />
            <span className="font-semibold text-amber-400">Follow-up Needed</span>
            <span className="ml-auto bg-amber-500/20 text-amber-400 text-xs font-bold px-2 py-0.5 rounded-full">
              {followUp.length}
            </span>
          </div>
          <div className="space-y-2">
            {followUp.slice(0, 5).map(item => (
              <div key={item.recipient_id} className="flex items-center gap-4 bg-slate-900/60 rounded-xl px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{item.name}</div>
                  <div className="text-xs text-slate-400 truncate">
                    {item.client_name} · <span className="text-amber-400">{item.title}</span>
                  </div>
                </div>
                <div className="text-xs text-slate-500 whitespace-nowrap">
                  Viewed {Math.round(item.hours_since_viewed)}h ago
                </div>
                <button
                  onClick={() => sendReminder(item.document_id)}
                  disabled={sending[item.document_id]}
                  className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap"
                >
                  <Bell size={12} /> Send Reminder
                </button>
                <button
                  onClick={() => navigate(`/documents/${item.document_id}`)}
                  className="text-slate-500 hover:text-white p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
                >
                  <ExternalLink size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h2 className="font-semibold text-white">Recent Documents</h2>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-500" />
            {['all','sent','partially_signed','completed','expired'].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                {STATUS_LABELS[s] || 'All'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : docs.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Clock size={32} className="mx-auto mb-3 opacity-30" />
            <p>No documents found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  {['Document','Client','Status','Progress','Last Activity','Actions'].map(h => (
                    <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {docs.map(doc => (
                  <tr key={doc.id} className="hover:bg-slate-800/30 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="font-medium text-white text-sm truncate max-w-[200px]">{doc.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{doc.sending_company}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-slate-300">{doc.client_name || '—'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[doc.status]}`}>
                        {STATUS_LABELS[doc.status]}
                      </span>
                      {doc.has_viewed_not_signed && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-400">
                          <Eye size={11} /> Viewed
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${doc.total_signers > 0 ? (doc.signed_count / doc.total_signers) * 100 : 0}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500">
                          {doc.signed_count}/{doc.total_signers}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {doc.last_activity
                        ? formatDistanceToNow(new Date(doc.last_activity), { addSuffix: true })
                        : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/documents/${doc.id}`)}
                          className="text-slate-500 hover:text-white p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
                        >
                          <ChevronRight size={15} />
                        </button>
                        {['sent','partially_signed'].includes(doc.status) && (
                          <button
                            onClick={() => sendReminder(doc.id)}
                            disabled={sending[doc.id]}
                            className="text-slate-500 hover:text-amber-400 p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
                            title="Send reminder"
                          >
                            <Bell size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
